import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useProctorGroups } from '@indeed/frontend-proctor-utils';
import { Box } from '@indeed/ifl-components';
import AutoApplySetupNavigation from './AutoApplySetupNavigation';
import MidSetupFeedbackLink from './mid-setup-feedback/MidSetupFeedbackLink';

/**
 * Layout component for Auto Apply Setup flow
 * Mobile-first design optimized for iPhone 14 Pro Max (430px)
 * Constrained to maxWidth: 450px and maxHeight: 100vh for optimal mobile experience
 *
 * On the complete page, paddingInline and gap are removed so the illustration
 * can render edge-to-edge and flush against the navigation bar.
 */
const AutoApplySetupLayout: React.FC = () => {
    const location = useLocation();
    const isCompletePage = location.pathname.endsWith('/complete');

    // The review and resume pages render their primary CTA as a viewport-fixed
    // floating footer (StickyActionFooter), but only in the progressive-automation bucket
    // (rnhp_auto_apply_banner_tst === 2). When that footer is present, reserve
    // extra bottom space so scrolled content and the feedback link clear it instead
    // of sitting underneath.
    const { getGroup } = useProctorGroups();
    const isProgressiveAutomation = getGroup('rnhp_auto_apply_banner_tst')?.value === 2;
    const path = location.pathname;
    const hasFloatingCta =
        isProgressiveAutomation &&
        (path.includes('/auto-apply-setup/review') || path.includes('/auto-apply-setup/resume'));

    return (
        <Box
            sx={{
                inlineSize: '100%',
                display: 'flex',
                justifyContent: 'center',
                overflow: 'hidden',
            }}
        >
            {/* Mobile-first container with fixed maxWidth */}
            <Box
                sx={{
                    inlineSize: '100%',
                    maxInlineSize: '25.125rem',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    paddingBlockStart: 'sc3',
                    paddingBlockEnd: hasFloatingCta ? 10 : 6,
                    gap: isCompletePage ? 0 : 6,
                }}
            >
                {/* Navigation owns its horizontal padding and returns null when
                    it would be empty (e.g. the progressive-automation variant
                    inside the Job Search app). Rendering it as a direct flex
                    child means a null nav reserves no height and no gap, so no
                    blank section is left at the top. */}
                <AutoApplySetupNavigation />

                {/* Page content with scroll */}
                <Box
                    sx={{
                        inlineSize: '100%',
                        flex: 1,
                        overflowY: 'auto',
                        paddingInline: isCompletePage ? 0 : 4,
                    }}
                >
                    <Outlet />
                </Box>

                {/* Footer: Mid-setup feedback link */}
                <Box sx={{ textAlign: 'center' }}>
                    <MidSetupFeedbackLink />
                </Box>
            </Box>
        </Box>
    );
};

export default AutoApplySetupLayout;
